const EMPTY_RELEASE_MARKERS = new Set(
  [
    'Keine nutzerrelevanten Änderungen in diesem Release.',
    'No user-facing changes in this release.',
    'Aucun changement visible pour l’utilisateur dans cette version.',
    'No hay cambios visibles para el usuario en esta versión.',
  ]
    .map((entry) => entry.toLowerCase().replace(/[.!?]+$/g, '').replace(/\s+/g, ' ').trim())
    .filter(Boolean),
);

const normalizeLanguage = (language) => String(language || 'de').toLowerCase().slice(0, 2);

const normalizeMarker = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[.!?]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const isEmptyMarker = (value) => EMPTY_RELEASE_MARKERS.has(normalizeMarker(value));

const ensurePeriod = (value) => {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  return /[.!?…]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

const stripTrailingPunctuation = (value) => String(value || '').replace(/[.!?…]+$/g, '').trim();

const stripPrefixes = (value) =>
  String(value || '')
    .replace(/^(feat|fix|chore|docs|refactor|perf|style|ci)\s*:\s*/i, '')
    .trim();

const FORMAT_REGEX = /\b(pdf|docx?|xlsx?|csv|json|xml|md|markdown|txt|rtf|html|pptx?)\b/gi;

const FORMAT_LABELS = {
  pdf: 'PDF',
  doc: 'Word',
  docx: 'Word',
  xls: 'Excel',
  xlsx: 'Excel',
  csv: 'CSV',
  json: 'JSON',
  xml: 'XML',
  md: 'Markdown',
  markdown: 'Markdown',
  txt: 'Text',
  rtf: 'RTF',
  html: 'HTML',
  ppt: 'PowerPoint',
  pptx: 'PowerPoint',
};

const extractFormats = (bullets) => {
  const formats = new Set();
  bullets.forEach((bullet) => {
    const cleaned = stripTrailingPunctuation(bullet).toLowerCase();
    const matches = cleaned.match(FORMAT_REGEX);
    if (matches) {
      matches.forEach((match) => formats.add(match.toLowerCase()));
    }
  });
  return Array.from(formats);
};

const buildFormatSummary = (bullets, language) => {
  const formats = extractFormats(bullets);
  const lang = normalizeLanguage(language);
  const hasMultiFormatHint = bullets.some((bullet) => {
    const lower = bullet.toLowerCase();
    if (lang === 'de') {
      return /mehrer(e|en)\s+formate/.test(lower) && /export/.test(lower);
    }
    if (lang === 'fr') {
      return /plusieurs\s+formats/.test(lower) && /export/.test(lower);
    }
    if (lang === 'es') {
      return /varios\s+formatos/.test(lower) && /export/.test(lower);
    }
    return /multiple\s+formats/.test(lower) && /export/.test(lower);
  });

  if (formats.length >= 2 || hasMultiFormatHint) {
    if (lang === 'de') return 'Neue Formate für Export verfügbar.';
    if (lang === 'fr') return 'Nouveaux formats d’export.';
    if (lang === 'es') return 'Nuevos formatos de exportación.';
    return 'New export formats available.';
  }
  return '';
};

const normalizeReleaseText = (value, language) => {
  if (!value) return '';
  const lang = normalizeLanguage(language);
  let text = stripPrefixes(value);
  text = text.replace(/^Aufgabe:\s*/i, '').trim();

  if (lang === 'de') {
    text = text.replace(/\bSie\b/g, '').replace(/\s{2,}/g, ' ').trim();
    text = text.replace(/^Fügen\s+Sie\s+/i, '');
    text = text.replace(/^Füge\s+/i, '');
    text = text.replace(/^Aktualisieren\s+Sie\s+/i, '');
    text = text.replace(/^Aktualisiere\s+/i, '');
    text = text.replace(/^Verbessere\s+/i, '');
    text = text.replace(/^Ersetze\s+/i, '');
    text = text.replace(/^Entferne\s+/i, '');
    text = text.replace(/^Korrigiere\s+/i, '');
    text = text.replace(/^Behebe\s+/i, '');
    text = text.replace(/^Fix:\s*/i, '');
    text = text.replace(/^Implementier\w*\s+/i, '');
    text = text.replace(/^Implementierung\s+von\s+/i, '');
    text = text.replace(/^Erweitere\s+/i, '');
    text = text.replace(/^Ergänze\s+/i, '');
    text = text.replace(/\bhinzugefügt\b/gi, '');
    text = text.replace(/\bhinzufügen\b/gi, '');
    text = text.replace(/\bhinzu\b$/i, '');
  } else if (lang === 'en') {
    text = text.replace(/^Add\s+/i, '');
    text = text.replace(/^Added\s+/i, '');
    text = text.replace(/^Update\s+/i, '');
    text = text.replace(/^Updated\s+/i, '');
    text = text.replace(/^Improve\s+/i, '');
    text = text.replace(/^Fix\s*/i, '');
    text = text.replace(/^Fix:\s*/i, '');
    text = text.replace(/^Implement\w*\s+/i, '');
  }

  text = text.replace(/\s{2,}/g, ' ').trim();
  return ensurePeriod(text);
};

const splitBullet = (value, language) => {
  const lang = normalizeLanguage(language);
  const separators =
    lang === 'de'
      ? /\s+(?:und|sowie|bzw\.?)\s+|,\s+/i
      : lang === 'fr'
        ? /\s+(?:et|ainsi que)\s+|,\s+/i
        : lang === 'es'
          ? /\s+(?:y|e|así como)\s+|,\s+/i
          : /\s+and\s+|,\s+/i;
  const raw = stripTrailingPunctuation(value);
  const parts = raw
    .split(separators)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 2) return [];
  const prefixMatch = parts[0].match(/^([A-Za-zÄÖÜäöüß]+:)\s+/);
  if (prefixMatch) {
    const prefix = prefixMatch[1];
    return parts.map((part, index) => {
      if (index === 0) return ensurePeriod(part);
      if (/^[A-Za-zÄÖÜäöüß]+:\s+/.test(part)) {
        return ensurePeriod(part);
      }
      return ensurePeriod(`${prefix} ${part}`);
    });
  }
  return parts.map((part) => ensurePeriod(part));
};

const isFormatListBullet = (value) => {
  const parts = String(value || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 2) return false;
  return parts.every((part) => {
    const normalized = part.toLowerCase();
    return Object.values(FORMAT_LABELS).some((label) => label.toLowerCase() === normalized);
  });
};

const ensureMinimumBullets = (bullets, language) => {
  if (bullets.length >= 2) return bullets;
  if (!bullets.length) return [];
  const lang = normalizeLanguage(language);
  if (!isFormatListBullet(bullets[0])) {
    const split = splitBullet(bullets[0], language);
    if (split.length >= 2) {
      return split;
    }
  }
  const detailPrefix =
    lang === 'de' ? 'Details:' : lang === 'fr' ? 'Détails:' : lang === 'es' ? 'Detalles:' : 'Details:';
  const detail = ensurePeriod(`${detailPrefix} ${stripTrailingPunctuation(bullets[0])}`);
  return [bullets[0], detail];
};

const buildSummary = (bullets, language) => {
  if (!bullets.length) return '';
  const formatSummary = buildFormatSummary(bullets, language);
  if (formatSummary) return formatSummary;
  const lang = normalizeLanguage(language);
  const conjunction =
    lang === 'de' ? 'und' : lang === 'fr' ? 'et' : lang === 'es' ? 'y' : 'and';
  const first = stripTrailingPunctuation(bullets[0]);
  const second = bullets[1] ? stripTrailingPunctuation(bullets[1]) : '';
  if (!second) return ensurePeriod(first);
  return ensurePeriod(`${first} ${conjunction} ${second}`);
};

const sortByCreatedAtDesc = (items) =>
  [...items].sort((a, b) => {
    const aTime = Date.parse(a?.createdAt ?? 0);
    const bTime = Date.parse(b?.createdAt ?? 0);
    return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime);
  });

export const prepareReleaseNotes = (releases, language) => {
  if (!Array.isArray(releases)) return [];
  const normalized = releases
    .map((release) => {
      const rawItems = Array.isArray(release?.items) ? release.items : [];
      const cleanedItems = rawItems
        .map((item) => normalizeReleaseText(item, language))
        .filter((item) => item && !isEmptyMarker(item));
      if (!cleanedItems.length) {
        return null;
      }
      const formatKeys = extractFormats(cleanedItems);
      const formatLabels = formatKeys
        .map((key) => FORMAT_LABELS[key] || key.toUpperCase())
        .filter(Boolean);
      const nonFormatBullets = cleanedItems.filter((item) => !item.toLowerCase().match(FORMAT_REGEX));
      let bullets = cleanedItems;
      if (formatLabels.length) {
        const formatBullet = formatLabels.join(', ');
        bullets = [formatBullet, ...nonFormatBullets];
        if (bullets.length < 2) {
          const lang = normalizeLanguage(language);
          const fallback =
            lang === 'de'
              ? 'Auswahl der Exportformate erweitert.'
              : lang === 'fr'
                ? 'Sélection des formats d’export élargie.'
                : lang === 'es'
                  ? 'Selección de formatos de exportación ampliada.'
                  : 'Export format selection expanded.';
          bullets.push(fallback);
        }
      }
      bullets = ensureMinimumBullets(bullets, language);
      if (!bullets.length) {
        return null;
      }
      const title = buildSummary(bullets, language);
      return {
        releaseId: release.releaseId ?? null,
        createdAt: release.createdAt ?? null,
        title,
        items: bullets,
        __prepared: true,
      };
    })
    .filter(Boolean);

  return sortByCreatedAtDesc(normalized);
};
